/**
 * Phase 4F — stale-while-rendering preview continuity helpers (UX only).
 * Phase 6L — newest-ready bulk follow reconciliation (identity-based).
 * Does not own blobs, scheduler, or export.
 */

export type ContinuityArt = {
  id: string;
  previewUrl?: string;
  displayUrl?: string;
  previewPending?: boolean;
};

/** True when the artwork has a displayable preview raster (not a blank pending card). */
export function isPreviewImageReady(art: ContinuityArt | null | undefined): boolean {
  if (!art) return false;
  const url = art.displayUrl || art.previewUrl;
  return typeof url === 'string' && url.trim().length > 0;
}

/**
 * Artwork is usable in the main preview for auto-follow purposes:
 * raster ready, or PNG failed and iframe fallback is allowed (previewPending === false).
 */
export function isArtworkFollowReady(art: ContinuityArt | null | undefined): boolean {
  if (!art) return false;
  if (isPreviewImageReady(art)) return true;
  return art.previewPending === false;
}

/** Gallery visual list: hide pending blank cards; preserve relative order of ready items. */
export function filterReadyPreviewGalleryItems<T extends ContinuityArt>(items: T[]): T[] {
  return items.filter((item) => isPreviewImageReady(item));
}

export type PreviewProgressCounts = {
  ready: number;
  pending: number;
  total: number;
};

export function countPreviewProgress(artQueue: ContinuityArt[]): PreviewProgressCounts {
  let ready = 0;
  let pending = 0;
  for (const art of artQueue) {
    if (isPreviewImageReady(art)) {
      ready += 1;
    } else if (art.previewPending) {
      pending += 1;
    }
  }
  return { ready, pending, total: artQueue.length };
}

/**
 * Status copy for UI outside the artwork plane.
 * null when nothing is preview-pending.
 */
export function formatPreviewRenderingStatus(
  progress: PreviewProgressCounts
): string | null {
  if (progress.pending <= 0) return null;
  if (progress.pending === 1) {
    return 'Rendering preview…';
  }
  return `Rendering previews · ${progress.ready} / ${progress.total} ready`;
}

/**
 * After prepending a new (often pending) artwork at index 0, shift the selection
 * index so the previously selected artwork stays selected.
 * previousLength = queue length before the prepend.
 */
export function shiftIndexAfterPrepend(
  currentIndex: number,
  previousLength: number
): number {
  if (previousLength <= 0) {
    // No prior artwork — do not select the pending item yet.
    return -1;
  }
  if (currentIndex < 0) {
    return -1;
  }
  return currentIndex + 1;
}

/**
 * Legacy Phase 4F exact-id gate (kept for older call sites/tests).
 * Phase 6L bulk follow uses {@link shouldReconcileFollowOnPreviewReady} instead.
 */
export function shouldAutoSelectOnReady(
  followLatestReadyId: string | null | undefined,
  finishedArtId: string
): boolean {
  return !!followLatestReadyId && followLatestReadyId === finishedArtId;
}

/** Phase 6L: any preview completion may reconcile while auto-follow is active. */
export function shouldReconcileFollowOnPreviewReady(followActive: boolean): boolean {
  return Boolean(followActive);
}

/**
 * Newest-first queue: first follow-ready artwork that belongs to the follow epoch.
 * Returns null when none are ready yet (sticky prior selection remains).
 */
export function findNewestReadyArtId(
  artQueue: ContinuityArt[],
  epochIds: ReadonlySet<string> | null | undefined
): string | null {
  if (!epochIds || epochIds.size === 0) return null;
  for (const art of artQueue) {
    if (!epochIds.has(art.id)) continue;
    if (isArtworkFollowReady(art)) {
      return art.id;
    }
  }
  return null;
}

/**
 * Whether selecting candidateId is a forward move (or first select) on a newest-first queue.
 * Prevents a late older preview callback from moving the main preview backwards.
 */
export function shouldAdvanceFollowSelection(
  artQueue: ContinuityArt[],
  currentSelectedId: string | null | undefined,
  candidateId: string | null | undefined
): boolean {
  if (!candidateId) return false;
  if (!currentSelectedId) return true;
  if (currentSelectedId === candidateId) return false;
  const candidateIdx = indexOfArtId(artQueue, candidateId);
  const currentIdx = indexOfArtId(artQueue, currentSelectedId);
  if (candidateIdx < 0) return false;
  if (currentIdx < 0) return true;
  // Newer artworks have lower indices.
  return candidateIdx < currentIdx;
}

/**
 * Resolve the index to select after a follow reconcile, or null if no change.
 * Identity-based: uses artwork ids, then derives index from the latest queue.
 */
export function resolveFollowSelectionIndex(
  artQueue: ContinuityArt[],
  epochIds: ReadonlySet<string> | null | undefined,
  currentSelectedId: string | null | undefined
): number | null {
  const newestId = findNewestReadyArtId(artQueue, epochIds);
  if (!newestId) return null;
  if (!shouldAdvanceFollowSelection(artQueue, currentSelectedId, newestId)) {
    return null;
  }
  const idx = indexOfArtId(artQueue, newestId);
  return idx >= 0 ? idx : null;
}

/**
 * Attach a preview URL onto an artwork by id (immutable).
 * Returns the previous array unchanged when the id is absent.
 */
export function attachPreviewToArtQueue<T extends ContinuityArt>(
  artQueue: T[],
  artId: string,
  url: string
): T[] {
  if (!artQueue.some((item) => item.id === artId)) {
    return artQueue;
  }
  return artQueue.map((item) =>
    item.id === artId
      ? {
          ...item,
          previewUrl: url,
          displayUrl: url,
          previewPending: false,
        }
      : item
  );
}

/**
 * Mark preview finished without a raster (iframe fallback path).
 */
export function markPreviewFailedInArtQueue<T extends ContinuityArt>(
  artQueue: T[],
  artId: string
): T[] {
  if (!artQueue.some((item) => item.id === artId)) {
    return artQueue;
  }
  return artQueue.map((item) =>
    item.id === artId ? { ...item, previewPending: false } : item
  );
}

/**
 * Resolve what to paint in the main preview.
 * Never returns a pending blank when another ready artwork exists.
 */
export function pickDisplayedArt<T extends ContinuityArt>(
  artQueue: T[],
  currentArtIndex: number
): T | undefined {
  if (!artQueue.length) return undefined;

  const selected =
    currentArtIndex >= 0 && currentArtIndex < artQueue.length
      ? artQueue[currentArtIndex]
      : undefined;

  if (selected && isPreviewImageReady(selected)) {
    return selected;
  }

  // Selected is pending / missing image — prefer older ready (higher index), then newer.
  if (selected) {
    for (let i = currentArtIndex + 1; i < artQueue.length; i++) {
      if (isPreviewImageReady(artQueue[i])) return artQueue[i];
    }
    for (let i = currentArtIndex - 1; i >= 0; i--) {
      if (isPreviewImageReady(artQueue[i])) return artQueue[i];
    }
  }

  return artQueue.find((a) => isPreviewImageReady(a));
}

/** Index of artId in queue, or -1. */
export function indexOfArtId(artQueue: ContinuityArt[], artId: string): number {
  return artQueue.findIndex((a) => a.id === artId);
}

/**
 * After deleting ids, clamp selection to a READY artwork when possible.
 */
export function resolveIndexAfterDeletion(
  artQueue: ContinuityArt[],
  preferredIndex: number
): number {
  if (!artQueue.length) return -1;
  if (
    preferredIndex >= 0 &&
    preferredIndex < artQueue.length &&
    isPreviewImageReady(artQueue[preferredIndex])
  ) {
    return preferredIndex;
  }
  const readyIdx = artQueue.findIndex((a) => isPreviewImageReady(a));
  return readyIdx >= 0 ? readyIdx : Math.max(0, Math.min(preferredIndex, artQueue.length - 1));
}
